/**
 * Error branching for checkout — n8n pattern.
 * Each error type gets its own classification, user message, and recovery action.
 * Instead of one generic "Order failed", route to specific handlers.
 */

const ERROR_TYPES = {
  PAYMENT_DECLINED: "payment_declined",
  ADDRESS_INVALID: "address_invalid",
  OUT_OF_STOCK: "out_of_stock",
  STORE_CREDIT_INSUFFICIENT: "store_credit_insufficient",
  RATE_LIMITED: "rate_limited",
  NETWORK: "network",
  UNKNOWN: "unknown",
};

const ERROR_BRANCHES = {
  [ERROR_TYPES.PAYMENT_DECLINED]: {
    title: "Payment issue",
    message: "Your payment couldn't be processed. Please check your payment details and try again.",
    recoveryAction: "change_payment",
    recoveryLabel: "Change payment method",
    retryable: true,
  },
  [ERROR_TYPES.ADDRESS_INVALID]: {
    title: "Invalid address",
    message: "We couldn't verify your shipping address. Please double-check and correct it.",
    recoveryAction: "fix_address",
    recoveryLabel: "Edit address",
    retryable: true,
  },
  [ERROR_TYPES.OUT_OF_STOCK]: {
    title: "Item unavailable",
    message: "One or more items in your cart are no longer in stock. Please update your cart.",
    recoveryAction: "back_to_cart",
    recoveryLabel: "Return to cart",
    retryable: false,
  },
  [ERROR_TYPES.STORE_CREDIT_INSUFFICIENT]: {
    title: "Store credit issue",
    message: "Your store credit balance has changed. The applied credit has been adjusted.",
    recoveryAction: "retry",
    recoveryLabel: "Try again",
    retryable: true,
  },
  [ERROR_TYPES.RATE_LIMITED]: {
    title: "Too many attempts",
    message: "Please wait a moment before trying again.",
    recoveryAction: "wait",
    recoveryLabel: "Try again in 30s",
    retryable: true,
    cooldownMs: 30000,
  },
  [ERROR_TYPES.NETWORK]: {
    title: "Connection error",
    message: "We lost connection. Check your internet and try again.",
    recoveryAction: "retry",
    recoveryLabel: "Retry",
    retryable: true,
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: "Something went wrong",
    message: "Your order couldn't be completed. No charges were applied. Please try again or contact support.",
    recoveryAction: "retry",
    recoveryLabel: "Try again",
    retryable: true,
  },
};

// Pattern matchers — order matters, first match wins
const CLASSIFIERS = [
  { type: ERROR_TYPES.OUT_OF_STOCK, patterns: [/out of stock/i, /insufficient (stock|inventory|quantity)/i, /not available/i, /no longer available/i, /sold out/i, /left in stock/i, /only \d+ unit/i] },
  { type: ERROR_TYPES.PAYMENT_DECLINED, patterns: [/payment.*fail/i, /payment.*decline/i, /card.*decline/i, /charge.*fail/i, /transaction.*fail/i, /insufficient funds/i] },
  { type: ERROR_TYPES.ADDRESS_INVALID, patterns: [/address.*invalid/i, /invalid.*address/i, /address.*verify/i, /address.*not found/i, /zip.*invalid/i, /state.*invalid/i] },
  { type: ERROR_TYPES.STORE_CREDIT_INSUFFICIENT, patterns: [/store.?credit/i, /credit.*insufficient/i, /insufficient.*credit/i, /credit.*balance/i, /credit.*fail/i] },
  { type: ERROR_TYPES.RATE_LIMITED, patterns: [/rate.?limit/i, /too many requests/i, /429/i, /throttle/i] },
  { type: ERROR_TYPES.NETWORK, patterns: [/fetch.*fail/i, /network/i, /timeout/i, /ECONNREFUSED/i, /Failed to fetch/i] },
];

export function classifyCheckoutError(errorMessage) {
  if (!errorMessage) return ERROR_TYPES.UNKNOWN;
  const msg = String(errorMessage);
  for (const { type, patterns } of CLASSIFIERS) {
    if (patterns.some(p => p.test(msg))) return type;
  }
  return ERROR_TYPES.UNKNOWN;
}

// Backend place_order reports exactly which item is short:
//   "Only <n> unit(s) left in stock: <item name>"
// Surface that in the customer-facing branch instead of the generic
// "one or more items" message (#6).
export function describeOutOfStock(rawMessage) {
  const m = /only\s+(\d+)\s+unit\(s\)\s+left in stock:\s*(.+)/i.exec(String(rawMessage || ""));
  if (!m) return null;
  const [, count, name] = m;
  const item = name.trim();
  return Number(count) === 0
    ? `"${item}" is sold out. Please remove it from your cart.`
    : `Only ${count} left of "${item}" — please lower the quantity in your cart.`;
}

export function getErrorBranch(errorType, rawMessage) {
  const branch = ERROR_BRANCHES[errorType] || ERROR_BRANCHES[ERROR_TYPES.UNKNOWN];
  if (errorType === ERROR_TYPES.OUT_OF_STOCK) {
    const detail = describeOutOfStock(rawMessage);
    if (detail) return { ...branch, message: detail };
  }
  return branch;
}

export { ERROR_TYPES };
